/**
 * A1 (PIN-1g→1l-PWA-TRANSPORT) — the LIVE HTTP transport that makes the PWA
 * actually send. Implements the two injected interfaces the outbox spine was
 * built to swap in (retry-unit.ts §26-38: "Real transport is 1l's live server
 * (gated); this batch wires a mock/dev implementation via tests"): this is
 * that live implementation.
 *
 * - `HttpEventTransport.postCommitEvent` → POST `${endpoint}/mark/message`
 *   with `Authorization: Bearer <token>`, mapping the working outbox
 *   `CaptureEnvelope` onto the coordinator's locked `Event` wire-shape
 *   (coordinator/models.py) and the ReplyEnvelope status vocabulary
 *   (accepted / transient-fail-retry / permanent-reject).
 * - `HttpBlobPutter.putBlob` → PUT the content-addressed media blob to the
 *   coordinator media-ingest endpoint (A2), idempotent by sha256 key.
 *
 * Config (endpoint + bearer) is read ONCE at construction from the baked
 * build env (`import.meta.env.VITE_MARK_ENDPOINT`) + pilot bearer — never
 * runtime-repointable (spec §8.3; vite.config.ts:7). The release image bakes
 * `/`, which normalises to an empty prefix and therefore uses the page's origin.
 */

import type { CaptureEnvelope, ReplyEnvelope } from "./types";
import type { EventTransport, BlobPutter, BlobPutScope } from "./retry-unit";
import { credentialFetch as fetch } from "../auth/credential-fetch";

/** The coordinator's known schema_version (routing.py KNOWN_SCHEMA_VERSIONS =
 * {"1.0"}). The outbox record carries its own internal envelope version; the
 * WIRE requires "1.0" or the coordinator permanent-rejects
 * (app.py:154-157 unknown_schema_version). Mapping happens here at the wire
 * boundary — the durable outbox record is never mutated for the wire. */
const WIRE_SCHEMA_VERSION = "1.0";

export interface TransportConfig {
  /** Baked build-time origin, or an empty string for same-origin. No trailing slash. */
  endpoint: string;
  /** Pilot bearer token (.mark-pilot.json stub_token). */
  token: string;
}

/** Read the live transport config from the baked build env. Throws loudly if
 * the endpoint is absent — a field build with no endpoint is a build error,
 * not a silent no-op (better to fail the boot than ship a PWA that can't
 * send and looks like it works). */
export function transportConfigFromEnv(
  env: Record<string, string | undefined>,
  /** This device's own bearer, when the person using it has been issued one.
   *
   * Passed in rather than read from a module global so this stays a pure
   * function of its inputs — the endpoint validation below is exactly the kind
   * of thing that must be testable without standing up IndexedDB.
   *
   * When absent, the baked pilot bearer is used, which is the pre-existing
   * behaviour and is what keeps already-installed phones working while people
   * are moved onto their own codes one at a time. */
  deviceToken?: string | null,
): TransportConfig {
  const endpoint = env.VITE_MARK_ENDPOINT;
  if (!endpoint) {
    throw new Error(
      "mark-transport: VITE_MARK_ENDPOINT is not set at build time — a field PWA " +
        "must be built with its coordinator origin baked in (spec §8.3).",
    );
  }
  const trimmed = endpoint.replace(/\/+$/, "");
  // The endpoint is the coordinator ORIGIN. Every call site appends its own
  // `/mark/...` path (`${endpoint}/mark/transcribe`, `/mark/logs`, …), so an
  // endpoint that already ends in `/mark` produces `/mark/mark/...` and every
  // request 404s.
  //
  // That failure is INVISIBLE at runtime, which is why it is worth a throw:
  // `transcribe()` maps any non-ok response to an empty transcript, and the read
  // hooks fall back to cache, so the app comes up looking healthy while the
  // photo lands and the transcript silently doesn't. Shipped live on 2026-07-28
  // from a mis-baked `VITE_MARK_ENDPOINT=.../mark`; grepping the bundle for the
  // host passed, because the host WAS there — just with one segment too many.
  if (/\/mark$/i.test(trimmed)) {
    throw new Error(
      `mark-transport: VITE_MARK_ENDPOINT must be the coordinator ORIGIN, not its ` +
        `/mark prefix (got "${endpoint}"). Call sites append /mark themselves, so ` +
        `this would request /mark/mark/... and 404 every call.`,
    );
  }
  return {
    endpoint: trimmed,
    token: env.VITE_MARK_AUTH_STUB === "1"
      ? deviceToken?.trim() || env.VITE_MARK_BEARER || ""
      : "",
  };
}

/**
 * Map an HTTP outcome to the PWA's ReplyEnvelope vocabulary (types.ts §92-102):
 * business logic NEVER branches on a raw status. 2xx→accepted (409 duplicate
 * is mapped server-side to accepted+idempotent per RULING-1l-DUP-IDEMPOTENT,
 * so we never see a bare 409); 4xx→permanent-reject; 5xx/network→transient.
 */
export function replyFromResponse(status: number, body: unknown): ReplyEnvelope {
  // The coordinator already speaks ReplyEnvelope — honor its status verbatim
  // when present, so server-side rulings (idempotent dedup, permanent codes)
  // are the source of truth rather than re-derived from the HTTP code.
  if (body && typeof body === "object" && "status" in body) {
    const b = body as { status: string; payload?: { idempotent?: boolean }; error?: unknown };
    if (b.status === "accepted") {
      return { status: "accepted", payload: b.payload };
    }
    if (b.status === "permanent-reject") {
      const err = b.error as { code?: string; message?: string } | string | undefined;
      const code = typeof err === "object" && err ? (err.code ?? "permanent_reject") : "permanent_reject";
      const message = typeof err === "object" && err ? (err.message ?? "") : String(err ?? "");
      return { status: "permanent-reject", error: { code, message } };
    }
    if (b.status === "transient-fail-retry") {
      return { status: "transient-fail-retry" };
    }
  }
  // Fallback: derive from the HTTP class if the body wasn't a ReplyEnvelope.
  if (status >= 200 && status < 300) return { status: "accepted" };
  // WI-B-05: a bare 401 is a STALE SESSION, not a permanent rejection of the
  // capture. Map it to auth-stale so the dispatcher holds the capture and pins
  // the A7 banner instead of killing the record (PIN-B-4). A 401 the server
  // dressed as a ReplyEnvelope is already honored verbatim above.
  if (status === 401) return { status: "auth-stale" };
  if (status >= 400 && status < 500) {
    return { status: "permanent-reject", error: { code: `http_${status}`, message: "client error" } };
  }
  return { status: "transient-fail-retry" };
}

export class HttpEventTransport implements EventTransport {
  constructor(private cfg: TransportConfig) {}

  async postCommitEvent(client_id: string, envelope: CaptureEnvelope, signal?: AbortSignal): Promise<ReplyEnvelope> {
    // Map the working CaptureEnvelope onto the coordinator's locked Event
    // wire-shape (coordinator/models.py Event). project_ref lives in the
    // payload bag (app.tsx WI-6a-3 / state-machine PROJECT_REF_KEY); the wire
    // Event needs it top-level, so lift it here at the boundary.
    const project_ref = (envelope.payload["project_ref"] as string | undefined) ?? "";
    const wireEvent = {
      type: envelope.type,
      schema_version: WIRE_SCHEMA_VERSION,
      tenant_id: envelope.tenant_id,
      ts: envelope.ts,
      project_ref,
      skill: envelope.skill,
      skill_version: envelope.skill_version,
      capture_id: envelope.capture_id,
      client_id,
      channel: "pwa",
      media: envelope.media.map((m) => ({
        role: m.role,
        media_ref: m.media_ref,
        mime: m.mime,
      })),
      payload: envelope.payload,
      source_refs: envelope.source_refs,
    };

    let res: Response;
    try {
      res = await fetch(`${this.cfg.endpoint}/mark/message`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.cfg.token}`,
        },
        credentials: "include",
        body: JSON.stringify(wireEvent),
        signal,
      });
    } catch {
      // Network throw / offline → transient; the dispatcher backs off + retries.
      return { status: "transient-fail-retry" };
    }

    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    return replyFromResponse(res.status, body);
  }
}

export class HttpBlobPutter implements BlobPutter {
  constructor(private cfg: TransportConfig) {}

  /** PUT one content-addressed part to the media-ingest endpoint (A2). The
   * key is `media/{capture_id}/{sha256}.{ext}`; the endpoint is idempotent by
   * that key (same sha256 = same bytes), so a resumed attempt is a safe no-op
   * server-side. Returns the resolved media_ref key the retry-unit stamps
   * onto the part. */
  async putBlob(
    capture_id: string,
    sha256: string,
    ext: string,
    mime: string,
    blob: Blob,
    scope?: BlobPutScope,
    signal?: AbortSignal,
  ): Promise<{ key: string }> {
    const key = `media/${capture_id}/${sha256}.${ext}`;
    const headers: Record<string, string> = {
      "Content-Type": mime,
      Authorization: `Bearer ${this.cfg.token}`,
    };
    // The server writes to MARK_MEDIA_ROOT/{tenant}/{project}/{key} so the
    // goalkeeper reader (tenant_ctx.rs:532) finds it. Scope is required for a
    // correct path; refuse rather than write to an unscoped location.
    if (!scope || !scope.tenant_id || !scope.project_ref) {
      throw new Error(
        `mark-transport: media PUT for ${key} has no tenant/project scope — refusing to write unscoped media.`,
      );
    }
    headers["X-Mark-Tenant"] = scope.tenant_id;
    headers["X-Mark-Project"] = scope.project_ref;
    const res = await fetch(`${this.cfg.endpoint}/mark/media/${key}`, {
      method: "PUT",
      headers,
      credentials: "include",
      body: blob,
      signal,
    });
    if (!res.ok && res.status !== 409) {
      // 409 = already present (idempotent re-PUT) — treated as success.
      throw new Error(`mark-transport: media PUT failed for ${key} (HTTP ${res.status})`);
    }
    return { key };
  }
}

/** Result of a transcribe round-trip. `low_confidence` is the §9a.2 signal the
 * capture UI uses to flag "verify this" — never to block the save. */
export type RoutingDetectionKind = "action" | "decision" | "material_change";
export interface RoutingDetectionResult {
  reviewedText: string;
  kinds: RoutingDetectionKind[];
}
export interface ReviewStageResult {
  review_token?: string;
  routing_detection?: RoutingDetectionResult;
}

export interface TranscriptResult extends ReviewStageResult {
  transcript: string;
  low_confidence: boolean;
}

/** B3: the record->upload->TRANSCRIBE->edit leg. After the audio blob is PUT to
 * /mark/media, the capture UI calls this to fetch an editable transcript BEFORE
 * the human confirms and the commit Event is queued. Deliberately fail-soft:
 * any error (network, server, STT disabled) resolves to an empty transcript so
 * the capture flow degrades to a blank editable field rather than breaking —
 * transcription is a trust aid, not a gate (§9a.2). */
export interface Transcriber {
  transcribe(
    scope: BlobPutScope,
    media_ref: string,
    signal?: AbortSignal,
    capture_id?: string,
  ): Promise<TranscriptResult>;
  stageReview?(
    scope: BlobPutScope,
    capture_id: string,
    attempted: string,
    signal?: AbortSignal,
  ): Promise<string | undefined>;
  stageReviewWithDetection?(
    scope: BlobPutScope,
    capture_id: string,
    attempted: string,
    signal?: AbortSignal,
  ): Promise<ReviewStageResult | undefined>;
}

const EMPTY_TRANSCRIPT: TranscriptResult = { transcript: "", low_confidence: false };

const ROUTING_DETECTION_KINDS = new Set<RoutingDetectionKind>([
  "action", "decision", "material_change",
]);

function parseReviewStage(body: Record<string, unknown>): ReviewStageResult {
  const result: ReviewStageResult = {};
  if (typeof body.review_token === "string") result.review_token = body.review_token;
  const raw = body.routing_detection;
  if (raw && typeof raw === "object") {
    const detection = raw as Record<string, unknown>;
    const reviewedText = detection.reviewed_text;
    const kinds = Array.isArray(detection.kinds)
      ? detection.kinds.filter((kind): kind is RoutingDetectionKind =>
          typeof kind === "string" && ROUTING_DETECTION_KINDS.has(kind as RoutingDetectionKind))
      : [];
    if (typeof reviewedText === "string") {
      result.routing_detection = { reviewedText, kinds };
    }
  }
  return result;
}

export class HttpTranscriber implements Transcriber {
  constructor(private cfg: TransportConfig) {}

  async transcribe(
    scope: BlobPutScope,
    media_ref: string,
    signal?: AbortSignal,
    capture_id?: string,
  ): Promise<TranscriptResult> {
    if (!scope || !scope.tenant_id || !scope.project_ref) return EMPTY_TRANSCRIPT;
    let res: Response;
    try {
      res = await fetch(`${this.cfg.endpoint}/mark/transcribe`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.cfg.token}`,
        },
        credentials: "include",
        body: JSON.stringify({
          media_ref,
          tenant_id: scope.tenant_id,
          project_ref: scope.project_ref,
          ...(capture_id ? { capture_id } : {}),
        }),
        signal,
      });
    } catch {
      return EMPTY_TRANSCRIPT; // offline / network throw -> blank editable field
    }
    if (!res.ok) return EMPTY_TRANSCRIPT;
    try {
      const body = (await res.json()) as Partial<TranscriptResult>;
      const review = parseReviewStage(body as Record<string, unknown>);
      return {
        transcript: typeof body.transcript === "string" ? body.transcript : "",
        low_confidence: body.low_confidence === true,
        ...review,
      };
    } catch {
      return EMPTY_TRANSCRIPT;
    }
  }

  async stageReview(
    scope: BlobPutScope,
    capture_id: string,
    attempted: string,
    signal?: AbortSignal,
  ): Promise<string | undefined> {
    return (await this.stageReviewWithDetection(scope, capture_id, attempted, signal))?.review_token;
  }

  async stageReviewWithDetection(
    scope: BlobPutScope,
    capture_id: string,
    attempted: string,
    signal?: AbortSignal,
  ): Promise<ReviewStageResult | undefined> {
    if (!scope.tenant_id || !scope.project_ref || !capture_id || !attempted.trim()) return undefined;
    try {
      const res = await fetch(`${this.cfg.endpoint}/mark/capture-review`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${this.cfg.token}` },
        credentials: "include",
        body: JSON.stringify({
          tenant_id: scope.tenant_id,
          project_ref: scope.project_ref,
          capture_id,
          attempted,
        }),
        signal,
      });
      if (!res.ok) return undefined;
      return parseReviewStage((await res.json()) as Record<string, unknown>);
    } catch {
      return undefined;
    }
  }
}
